/**
 * InviteClientModal Component
 * Feature: 012-care-seeker-invite-signup (T049)
 *
 * Modal dialog for Financial Advisors and Service Providers
 * to invite care seekers to join bQuest.
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { createInvite, isEmailConflictError } from '@/features/authentication/api/inviteApi';
import type { InviteResponse } from '@/features/authentication/types/invite';

type InviteClientModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (invitation: InviteResponse) => void;
};

type FormData = {
  firstName: string;
  lastName: string;
  email: string;
};

type FormErrors = {
  firstName?: string;
  lastName?: string;
  email?: string;
  general?: string;
};

const INITIAL_FORM_DATA: FormData = {
  firstName: '',
  lastName: '',
  email: '',
};

export function InviteClientModal({
  open,
  onOpenChange,
  onSuccess,
}: InviteClientModalProps) {
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM_DATA);
    setErrors({});
    setSuccessMessage('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setSuccessMessage('');

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await createInvite({
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim().toLowerCase(),
      });

      setSuccessMessage('Invitation sent successfully!');

      // Reset form
      setFormData(INITIAL_FORM_DATA);

      // Call success callback
      if (onSuccess) {
        onSuccess(result);
      }

      // Close modal after short delay
      setTimeout(() => {
        onOpenChange(false);
        setSuccessMessage('');
      }, 1500);
    } catch (error) {
      if (isEmailConflictError(error)) {
        const errorMessage = (error as { message?: string }).message;
        // Display the specific error message from the API
        if (errorMessage?.includes('already registered')) {
          setErrors({
            general: 'This person is already registered on bQuest.',
          });
        } else if (errorMessage?.includes('pending invitation')) {
          setErrors({
            general: 'A pending invitation already exists for this email.',
          });
        } else {
          setErrors({
            general: errorMessage || 'This email cannot be invited.',
          });
        }
      } else {
        setErrors({
          general: 'Failed to send invitation. Please try again.',
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    resetForm();
    onOpenChange(false);
  };

  const handleInputChange = (field: keyof FormData) => (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    // Clear field-specific error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="text-gray-900">Invite Client</DialogTitle>
          <DialogDescription className="text-gray-600">
            Enter your client's information. They will receive an email invitation
            to join bQuest.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            {/* First Name Field */}
            <div className="grid gap-2">
              <Label htmlFor="firstName" className="text-gray-700">
                First Name
              </Label>
              <Input
                id="firstName"
                value={formData.firstName}
                onChange={handleInputChange('firstName')}
                placeholder="John"
                className={errors.firstName ? 'border-red-500' : ''}
                disabled={isSubmitting}
                autoComplete="off"
              />
              {errors.firstName && (
                <p className="text-sm text-red-600">{errors.firstName}</p>
              )}
            </div>

            {/* Last Name Field */}
            <div className="grid gap-2">
              <Label htmlFor="lastName" className="text-gray-700">
                Last Name
              </Label>
              <Input
                id="lastName"
                value={formData.lastName}
                onChange={handleInputChange('lastName')}
                placeholder="Doe"
                className={errors.lastName ? 'border-red-500' : ''}
                disabled={isSubmitting}
                autoComplete="off"
              />
              {errors.lastName && (
                <p className="text-sm text-red-600">{errors.lastName}</p>
              )}
            </div>

            {/* Email Field */}
            <div className="grid gap-2">
              <Label htmlFor="email" className="text-gray-700">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                placeholder="john@example.com"
                className={errors.email ? 'border-red-500' : ''}
                disabled={isSubmitting}
                autoComplete="off"
              />
              {errors.email && (
                <p className="text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            {/* General Error Message */}
            {errors.general && (
              <Alert className="border-red-200 bg-red-50">
                <AlertDescription className="text-red-800">
                  {errors.general}
                </AlertDescription>
              </Alert>
            )}

            {/* Success Message */}
            {successMessage && (
              <Alert className="border-green-200 bg-green-50">
                <AlertDescription className="text-green-800">
                  {successMessage}
                </AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-[#026857] hover:bg-[#025247]"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send Invitation'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
